import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type RealtimeTable = 
    | 'bot_positions' 
    | 'strategy_tier_config' 
    | 'category_tier_config' 
    | 'market_data_cache' 
    | 'system_config' 
    | 'risk_events'
    | 'profiles'
    | 'user_dashboard_stats'
    | 'daily_pnl_tracking'
    | 'portfolio_heat'
    | 'system_settings'
    | 'user_settings'
    | 'strategies_cache';
type RealtimeEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

type Listener = (table: string, event: string, payload: any) => void;

interface RealtimeContextType {
    isConnected: boolean;
    /**
     * Decoupled emitter to prevent context-wide re-render storms.
     * Components should use useRealtimeEvent instead of consuming this directly.
     */
    subscribe: (listener: Listener) => () => void;
}

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
    const [isConnected, setIsConnected] = useState(false);
    const retryCountRef = useRef(0);

    // Use a Ref to store listeners to prevent triggering re-renders when listeners change
    const listenersRef = useRef<Set<Listener>>(new Set());
    const debounceTimersRef = useRef<Record<string, number>>({});

    const subscribe = (listener: Listener) => {
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    };

    useEffect(() => {
        console.log('[Realtime] Initializing Global Connection...');

        let metaChannel: any = null;

        const emit = (table: string, event: string, payload: any) => {
            listenersRef.current.forEach(callback => {
                try {
                    callback(table, event, payload);
                } catch (err) {
                    console.error(`[Realtime] Listener error for table ${table}:`, err);
                }
            });
        };

        const debouncedEmit = (table: string, event: string, payload: any, delay: number = 300) => {
            if (debounceTimersRef.current[table]) {
                window.clearTimeout(debounceTimersRef.current[table]);
            }
            debounceTimersRef.current[table] = window.setTimeout(() => {
                emit(table, event, payload);
                delete debounceTimersRef.current[table];
            }, delay);
        };

        const isConnectedRef = { current: false };

        // Sovereign Architecture: Static / Meta channel only.
        // High-frequency trading & execution data flows 100% via Sovereign VPS SSE (/events).
        const connectChannels = (force = false) => {
            if (!force && metaChannel && isConnectedRef.current) {
                console.log('[Realtime] Channels already connected and active. Skipping redundant re-sync.');
                return;
            }

            // Clean up existing first
            if (metaChannel) {
                console.log('[Realtime] Cleared old Meta Channel');
                supabase.removeChannel(metaChannel);
                metaChannel = null;
            }

            console.log('[Realtime] Connecting Static Meta Channel...');

            // Static / Meta (Low frequency system config updates)
            metaChannel = supabase.channel('meta-db-changes')
                .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, (p) => debouncedEmit('profiles', p.eventType, p, 1000))
                .on('postgres_changes', { event: '*', schema: 'public', table: 'system_config' }, (p) => debouncedEmit('system_config', p.eventType, p, 2000))
                .on('postgres_changes', { event: '*', schema: 'public', table: 'strategy_tier_config' }, (p) => debouncedEmit('strategy_tier_config', p.eventType, p, 1000))
                .on('postgres_changes', { event: '*', schema: 'public', table: 'category_tier_config' }, (p) => debouncedEmit('category_tier_config', p.eventType, p, 1000))
                .on('postgres_changes', { event: '*', schema: 'public', table: 'system_settings' }, (p) => debouncedEmit('system_settings', p.eventType, p, 1000))
                .on('postgres_changes', { event: '*', schema: 'public', table: 'user_settings' }, (p) => debouncedEmit('user_settings', p.eventType, p, 1000))
                .subscribe((status) => {
                    if (status === 'SUBSCRIBED') {
                        console.log('[Realtime] Meta Channel Connected');
                        isConnectedRef.current = true;
                        setIsConnected(true);
                    } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
                        console.warn(`[Realtime] Meta Channel disconnected. Status: ${status}`);
                        isConnectedRef.current = false;
                        setIsConnected(false);
                    }
                });
        };

        connectChannels();

        // Listen for visibility changes (tab wake-up / mobile resume)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && !isConnectedRef.current) {
                console.log('[Realtime] App resumed / Tab focused and disconnected. Re-syncing channels...');
                connectChannels();
            }
        };

        // Listen for online status (network recovered)
        const handleOnline = () => {
            console.log('[Realtime] Network back online. Re-establishing sockets...');
            connectChannels();
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('online', handleOnline);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('online', handleOnline);
            if (metaChannel) supabase.removeChannel(metaChannel);
        };
    }, []);

    return (
        <RealtimeContext.Provider value={{ isConnected, subscribe }}>
            {children}
        </RealtimeContext.Provider>
    );
}

export function useRealtimeContext() {
    const context = useContext(RealtimeContext);
    if (context === undefined) {
        throw new Error('useRealtimeContext must be used within a RealtimeProvider');
    }
    return context;
}

/**
 * Hook to subscribe to realtime events for a specific table.
 * Optimized to prevent re-render storms by using a direct listener pattern.
 * 
 * @param table Table to listen to
 * @param callback Function to call when event occurs
 * @param filterEvent Optional event type filter (INSERT, UPDATE, DELETE)
 */
export function useRealtimeEvent(
    table: RealtimeTable,
    callback: (payload: any) => void,
    filterEvent?: RealtimeEvent
) {
    const { subscribe } = useRealtimeContext();

    // Keep callback ref fresh to avoid stale closures without requiring useCallback from user
    const callbackRef = useRef(callback);
    callbackRef.current = callback;

    useEffect(() => {
        const listener: Listener = (t, e, p) => {
            if (t === table) {
                if (!filterEvent || filterEvent === '*' || e === filterEvent) {
                    callbackRef.current(p);
                }
            }
        };

        return subscribe(listener);
    }, [table, filterEvent, subscribe]);
}

